import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback } from 'react';
import { FaGavel } from 'react-icons/fa';
import { BidPanelContainer, CurrentBidDisplay, MinBidLabel, BidInputRow, BidInput, BidButton, QuickBidButtons, QuickBidButton, BidError, BidSuccess, } from '../../pages/AuctionHouse/AuctionDetail.styles';
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};
const AuctionBidPanel = ({ currentBid, minimumBid, bidCount, status, onPlaceBid, }) => {
    const [bidAmount, setBidAmount] = useState(String(minimumBid));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const canBid = status === 'active' || status === 'grace_period';
    // Update bid amount when minimum changes
    React.useEffect(() => {
        setBidAmount(String(minimumBid));
    }, [minimumBid]);
    const handlePlaceBid = useCallback(async () => {
        const amount = Number(bidAmount);
        if (isNaN(amount) || amount <= 0) {
            setError('Please enter a valid bid amount');
            return;
        }
        if (amount < minimumBid) {
            setError(`Minimum bid is ${formatCurrency(minimumBid)}`);
            return;
        }
        setIsSubmitting(true);
        setError(null);
        setSuccess(null);
        try {
            const result = await onPlaceBid(amount);
            if (result.success) {
                setSuccess(`Bid of ${formatCurrency(amount)} placed successfully!`);
                // Clear success message after a few seconds
                setTimeout(() => setSuccess(null), 4000);
            }
            else {
                setError(result.error || 'Failed to place bid');
            }
        }
        catch (err) {
            setError(err.message || 'Failed to place bid');
        }
        finally {
            setIsSubmitting(false);
        }
    }, [bidAmount, minimumBid, onPlaceBid]);
    const setQuickBid = useCallback((amount) => {
        setBidAmount(String(amount));
        setError(null);
    }, []);
    const fivePercentAbove = Math.ceil(currentBid * 1.05);
    const tenPercentAbove = Math.ceil(currentBid * 1.10);
    return (_jsxs(BidPanelContainer, { children: [_jsxs(CurrentBidDisplay, { children: [_jsx("div", { className: "bid-label", children: "Current Bid" }), _jsx("div", { className: "bid-amount", children: formatCurrency(currentBid) }), _jsxs("div", { className: "bid-count", children: [bidCount, " bid", bidCount !== 1 ? 's' : '', " placed"] })] }), _jsxs(MinBidLabel, { children: ["Minimum next bid: ", _jsx("strong", { children: formatCurrency(minimumBid) })] }), _jsxs(QuickBidButtons, { children: [_jsx(QuickBidButton, { onClick: () => setQuickBid(minimumBid), disabled: !canBid, children: "Min Bid" }), _jsxs(QuickBidButton, { onClick: () => setQuickBid(fivePercentAbove), disabled: !canBid, children: ["+5% (", formatCurrency(fivePercentAbove), ")"] }), _jsxs(QuickBidButton, { onClick: () => setQuickBid(tenPercentAbove), disabled: !canBid, children: ["+10% (", formatCurrency(tenPercentAbove), ")"] })] }), _jsxs(BidInputRow, { children: [_jsx(BidInput, { type: "number", value: bidAmount, onChange: (e) => {
                            setBidAmount(e.target.value);
                            setError(null);
                        }, onKeyDown: (e) => {
                            if (e.key === 'Enter' && canBid && !isSubmitting) {
                                handlePlaceBid();
                            }
                        }, placeholder: `Min ${formatCurrency(minimumBid)}`, disabled: !canBid || isSubmitting, min: minimumBid, step: 1 }), _jsxs(BidButton, { onClick: handlePlaceBid, disabled: !canBid || isSubmitting, "$loading": isSubmitting, children: [_jsx(FaGavel, {}), isSubmitting ? 'Bidding...' : 'Place Bid'] })] }), !canBid && status && (_jsxs(BidError, { children: ["Bidding is not available. Auction status: ", status.replace(/_/g, ' ')] })), error && _jsx(BidError, { children: error }), success && _jsx(BidSuccess, { children: success })] }));
};
export default AuctionBidPanel;
